import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LANGUAGES } from '../../shared/languages';
import type { Allergen, DietaryTag } from '../../shared/menu';
import { useOrderHistory, useSettings, type OnboardingChoice } from '../lib/store';
import { defaultsFor, IDENTITY_CARDS } from '../lib/identity';
import { useT } from '../lib/i18n';
import { fetchMe, fetchOrders, logout, syncOrder } from '../lib/api';
import { EmailSignIn } from '../components/EmailSignIn';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { MenuListItem } from '../components/MenuListItem';
import { OrderListItem } from '../components/OrderListItem';
import { useSavedMenus, useSavedOrders } from '../lib/savedLists';

const ALLERGEN_LABELS: Record<Allergen, string> = {
  nuts: 'Tree nuts',
  peanut: 'Peanuts',
  shellfish: 'Shellfish',
  fish: 'Fish',
  egg: 'Egg',
  soy: 'Soy',
  wheat: 'Wheat / Gluten',
  sesame: 'Sesame',
  dairy: 'Dairy',
  pork: 'Pork',
  alcohol: 'Alcohol',
};

const DIETARY_LABELS: Record<DietaryTag, string> = {
  vegan: 'Vegan',
  vegetarian: 'Vegetarian',
  pescatarian: 'Pescatarian',
  halal: 'Halal',
  kosher: 'Kosher',
  gluten_free: 'Gluten-free',
  dairy_free: 'Dairy-free',
};

const ALL_ALLERGENS: Allergen[] = ['nuts', 'peanut', 'shellfish', 'fish', 'egg', 'soy', 'wheat', 'sesame', 'dairy', 'pork', 'alcohol'];
const ALL_DIETARY: DietaryTag[] = ['vegan', 'vegetarian', 'pescatarian', 'halal', 'kosher', 'gluten_free', 'dairy_free'];

const PAGE_SIZE = 20;

export default function MeScreen() {
  const t = useT();
  const qc = useQueryClient();
  const targetLanguage = useSettings((s) => s.targetLanguage);
  const setTargetLanguage = useSettings((s) => s.setTargetLanguage);
  const dietaryFlags = useSettings((s) => s.dietaryFlags);
  const toggleDietary = useSettings((s) => s.toggleDietary);
  const allergens = useSettings((s) => s.hardFilterAllergens);
  const toggleAllergen = useSettings((s) => s.toggleAllergenFilter);
  const hideUncertainAllergens = useSettings((s) => s.hideUncertainAllergens);
  const setHideUncertainAllergens = useSettings((s) => s.setHideUncertainAllergens);
  const onboardingChoice = useSettings((s) => s.onboardingChoice);
  const setOnboardingChoice = useSettings((s) => s.setOnboardingChoice);
  const setHardFilterAllergens = useSettings((s) => s.setHardFilterAllergens);
  const setDietaryFlags = useSettings((s) => s.setDietaryFlags);
  const setDietaryReason = useSettings((s) => s.setDietaryReason);

  const [signInOpen, setSignInOpen] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [shownMenus, setShownMenus] = useState(PAGE_SIZE);
  const [shownOrders, setShownOrders] = useState(PAGE_SIZE);

  const meQ = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const signedIn = !!meQ.data?.user;

  const { list: menus, loading: menusLoading, isUnauthorized } = useSavedMenus();
  const { list: orders } = useSavedOrders();

  // Pull server orders into the local store so the unified list stays current
  // even when network is flaky after first load.
  const ordersQ = useQuery({ queryKey: ['orders'], queryFn: fetchOrders, retry: false });
  const ordersUnauthorized = ordersQ.error instanceof Error && 'status' in ordersQ.error && (ordersQ.error as { status: number }).status === 401;
  const hydrateOrders = useOrderHistory((s) => s.hydrate);
  const markSynced = useOrderHistory((s) => s.markSynced);
  const unsynced = useOrderHistory((s) => s.unsyncedOrders);
  useEffect(() => {
    if (ordersQ.data?.orders) hydrateOrders(ordersQ.data.orders);
  }, [ordersQ.data, hydrateOrders]);

  const [importPromptOpen, setImportPromptOpen] = useState(false);
  const [importDismissed, setImportDismissed] = useState(false);
  const unsyncedCount = unsynced().length;
  useEffect(() => {
    if (
      ordersQ.isSuccess &&
      !ordersUnauthorized &&
      unsyncedCount > 0 &&
      !importDismissed &&
      !importPromptOpen
    ) {
      setImportPromptOpen(true);
    }
  }, [ordersQ.isSuccess, ordersUnauthorized, unsyncedCount, importDismissed, importPromptOpen]);

  const handleImport = async () => {
    setImportPromptOpen(false);
    const list = unsynced();
    for (const o of list) {
      try {
        const r = await syncOrder(o);
        markSynced(o.id, r.syncedAt);
      } catch {
        break;
      }
    }
    qc.invalidateQueries({ queryKey: ['orders'] });
  };

  const handleSignOut = async () => {
    await logout();
    qc.invalidateQueries({ queryKey: ['me'] });
    qc.invalidateQueries({ queryKey: ['saved'] });
    qc.invalidateQueries({ queryKey: ['orders'] });
  };

  const applyIdentity = (choice: OnboardingChoice) => {
    const d = defaultsFor(choice);
    setOnboardingChoice(choice);
    setHardFilterAllergens(d.allergens);
    setDietaryFlags(d.dietary);
    setDietaryReason(d.reason);
  };

  const visibleMenus = menus.slice(0, shownMenus);
  const visibleOrders = orders.slice(0, shownOrders);

  return (
    <div className="min-h-dvh safe-top safe-bottom container-app pb-12">
      <header className="flex items-center gap-3 pt-4 pb-6">
        <Link
          to="/"
          aria-label="Back"
          className="rounded-full inline-flex items-center justify-center"
          style={{ minWidth: 44, minHeight: 44, color: 'var(--color-primary)' }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-xl font-semibold" style={{ letterSpacing: '-0.01em' }}>You</h1>
      </header>

      {/* Account */}
      <section className="pb-8">
        <h2 style={sectionLabel}>Account</h2>
        <div
          className="rounded-md"
          style={{
            background: 'var(--color-surface)',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          {meQ.isLoading ? (
            <span style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>Loading…</span>
          ) : signedIn ? (
            <>
              <div style={{ overflow: 'hidden', flex: 1 }}>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  Signed in
                  {meQ.data?.user?.isAdmin && (
                    <span
                      style={{
                        marginLeft: 8,
                        padding: '1px 8px',
                        borderRadius: 9999,
                        fontSize: 10,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        background: 'var(--color-primary)',
                        color: 'var(--color-bg)',
                        fontWeight: 600,
                        verticalAlign: 'middle',
                      }}
                    >
                      Admin
                    </span>
                  )}
                </div>
                <div
                  style={{ fontSize: 14, color: 'var(--color-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  className="mono"
                >
                  {meQ.data?.user?.email ?? '—'}
                </div>
              </div>
              <button
                onClick={handleSignOut}
                style={{
                  padding: '8px 14px',
                  borderRadius: 9999,
                  border: '1px solid var(--color-divider)',
                  background: 'transparent',
                  color: 'var(--color-text-secondary)',
                  fontSize: 13,
                  minHeight: 36,
                }}
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Not signed in</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 2 }}>
                  Sign in to save menus and orders across devices.
                </div>
              </div>
              <button
                onClick={() => setSignInOpen(true)}
                className="btn-primary"
                style={{ padding: '8px 16px', minHeight: 36, fontSize: 13, whiteSpace: 'nowrap' }}
              >
                Sign in
              </button>
            </>
          )}
        </div>

        {meQ.data?.user?.isAdmin && (
          <a
            href="/admin/"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 10,
              padding: '14px 16px',
              borderRadius: 8,
              background: 'var(--color-surface)',
              color: 'var(--color-primary)',
              textDecoration: 'none',
              fontSize: 14,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <path d="M3 9h18" />
                <path d="M8 14h3M8 17h6" />
              </svg>
              Admin dashboard
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-text-secondary)' }}>
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </a>
        )}
      </section>

      {/* Menus (paged) */}
      <section className="pb-8">
        <div style={sectionHeaderRow}>
          <h2 style={sectionLabel}>Menus</h2>
          {menus.length > 0 && (
            <span style={countBadge}>{menus.length}</span>
          )}
        </div>

        {menusLoading && menus.length === 0 && (
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
        )}

        {!menusLoading && menus.length === 0 && (
          <div style={{ paddingTop: 8, color: 'var(--color-text-secondary)' }}>
            <p style={{ marginBottom: 8 }}>You haven't scanned any menus yet.</p>
            <Link to="/scan" style={{ color: 'var(--color-accent)', fontWeight: 500 }}>
              Scan a menu →
            </Link>
          </div>
        )}

        {menus.length > 0 && (
          <>
            {isUnauthorized && (
              <div
                style={{
                  marginBottom: 12,
                  padding: '10px 14px',
                  borderRadius: 10,
                  background: 'var(--color-surface)',
                  border: '1px dashed var(--color-divider)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  fontSize: 13,
                }}
              >
                <span style={{ color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                  Showing menus on this device. Sign in to sync across devices.
                </span>
                <button
                  onClick={() => setShowSignIn(true)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 9999,
                    border: '1px solid var(--color-primary)',
                    background: 'transparent',
                    color: 'var(--color-primary)',
                    fontSize: 12,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Sign in
                </button>
              </div>
            )}
            <ul style={listStyle}>
              {visibleMenus.map((m) => (
                <MenuListItem key={m.menuId} menu={m} />
              ))}
            </ul>
            <PageProgress
              shown={Math.min(shownMenus, menus.length)}
              total={menus.length}
              onShowMore={() => setShownMenus((n) => n + PAGE_SIZE)}
            />
          </>
        )}
      </section>

      {/* Orders (paged) */}
      <section className="pb-8">
        <div style={sectionHeaderRow}>
          <h2 style={sectionLabel}>Orders</h2>
          {orders.length > 0 && (
            <span style={countBadge}>{orders.length}</span>
          )}
        </div>

        {orders.length === 0 && (
          <p style={{ color: 'var(--color-text-secondary)' }}>{t('orders.empty')}</p>
        )}

        {orders.length > 0 && (
          <>
            <ul style={listStyle}>
              {visibleOrders.map((o) => (
                <OrderListItem key={o.id} order={o} />
              ))}
            </ul>
            <PageProgress
              shown={Math.min(shownOrders, orders.length)}
              total={orders.length}
              onShowMore={() => setShownOrders((n) => n + PAGE_SIZE)}
            />
          </>
        )}
      </section>

      {/* Identity */}
      <section className="pb-8">
        <h2 style={sectionLabel}>Identity</h2>
        <p className="text-xs pb-3" style={{ color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          Picking one applies the matching defaults below — hard-filter allergens, dietary preference, and the strict-tag reason. You can fine-tune everything afterward.
        </p>
        <div className="flex flex-col gap-2">
          {IDENTITY_CARDS.map((card) => {
            const active = onboardingChoice === card.choice;
            return (
              <button
                key={card.choice}
                onClick={() => applyIdentity(card.choice)}
                className="text-left"
                style={{
                  padding: '12px 14px',
                  borderRadius: 10,
                  border: `1.5px solid ${active ? 'var(--color-primary)' : 'var(--color-divider)'}`,
                  background: active ? 'var(--color-surface)' : 'transparent',
                  color: 'var(--color-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  minHeight: 56,
                }}
              >
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{card.label}</span>
                  <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 400 }}>{card.hint}</span>
                </span>
                {active && (
                  <span
                    style={{
                      fontSize: 10,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      color: 'var(--color-bg)',
                      background: 'var(--color-primary)',
                      padding: '3px 8px',
                      borderRadius: 9999,
                    }}
                  >
                    Active
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Language */}
      <section className="pb-8">
        <h2 style={sectionLabel}>{t('settings.language')}</h2>
        <div className="rounded-md overflow-hidden" style={{ background: 'var(--color-surface)' }}>
          <select
            value={targetLanguage}
            onChange={(e) => setTargetLanguage(e.target.value)}
            className="w-full"
            style={{
              padding: '14px 16px',
              background: 'transparent',
              border: 'none',
              color: 'var(--color-primary)',
              fontSize: 16,
              appearance: 'none',
            }}
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.nativeName} — {l.name}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Dietary */}
      <section className="pb-8">
        <h2 style={sectionLabel}>{t('settings.dietary')}</h2>
        <div className="flex flex-wrap gap-2">
          {ALL_DIETARY.map((d) => {
            const active = dietaryFlags.includes(d);
            return (
              <button
                key={d}
                onClick={() => toggleDietary(d)}
                style={{
                  padding: '10px 16px',
                  borderRadius: 9999,
                  border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-divider)'}`,
                  background: active ? 'var(--color-primary)' : 'transparent',
                  color: active ? 'var(--color-bg)' : 'var(--color-primary)',
                  fontSize: 14,
                  minHeight: 44,
                }}
              >
                {DIETARY_LABELS[d]}
              </button>
            );
          })}
        </div>
      </section>

      {/* Allergens */}
      <section className="pb-8">
        <h2 style={sectionLabel}>{t('settings.allergens')}</h2>
        <p className="text-xs pb-3" style={{ color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          Dishes containing these will be flagged in red and hidden by default. You can show them anyway when browsing.
        </p>
        <div className="flex flex-wrap gap-2">
          {ALL_ALLERGENS.map((a) => {
            const active = allergens.includes(a);
            return (
              <button
                key={a}
                onClick={() => toggleAllergen(a)}
                style={{
                  padding: '10px 16px',
                  borderRadius: 9999,
                  border: `1px solid ${active ? 'var(--color-danger)' : 'var(--color-divider)'}`,
                  background: active ? 'var(--color-danger)' : 'transparent',
                  color: active ? '#fff' : 'var(--color-primary)',
                  fontSize: 14,
                  minHeight: 44,
                }}
              >
                {ALLERGEN_LABELS[a]}
              </button>
            );
          })}
        </div>
        {allergens.includes('wheat') && (
          <div
            role="note"
            style={{
              marginTop: 12,
              padding: '10px 14px',
              borderRadius: 8,
              background: 'rgba(196,51,43,0.06)',
              border: '1px solid rgba(196,51,43,0.25)',
              fontSize: 13,
              color: 'var(--color-primary)',
              lineHeight: 1.45,
            }}
          >
            <strong>Celiac?</strong> Add <em>No cross-contamination</em> on each dish you tag — shared pans and surfaces matter.{' '}
            <Link to="/safety" style={{ color: 'var(--color-danger)', textDecoration: 'underline', fontWeight: 600 }}>
              Read the safety guide
            </Link>
            .
          </div>
        )}
        {allergens.length > 0 && (
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              marginTop: 12,
              padding: '12px 14px',
              borderRadius: 8,
              background: 'var(--color-surface)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={hideUncertainAllergens}
              onChange={(e) => setHideUncertainAllergens(e.target.checked)}
              style={{ marginTop: 2, width: 18, height: 18, accentColor: 'var(--color-danger)', flexShrink: 0 }}
            />
            <span style={{ fontSize: 14, color: 'var(--color-primary)', lineHeight: 1.4 }}>
              <strong>Also hide uncertain matches</strong>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                Treats &quot;maybe contains&quot; the same as confirmed. For severe allergies and celiac.
              </span>
            </span>
          </label>
        )}
      </section>

      {/* About */}
      <section className="pt-2">
        <h2 style={sectionLabel}>{t('settings.about')}</h2>
        <p className="text-sm pb-3" style={{ color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
          Read foreign menus. Build orders. Show the waiter. Allergen detection is best-effort — always confirm with the kitchen.
        </p>
        <div className="flex flex-col gap-2">
          <Link
            to="/safety"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px',
              borderRadius: 8,
              background: 'var(--color-surface)',
              color: 'var(--color-primary)',
              textDecoration: 'none',
              fontSize: 14,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-danger)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              Safety guide
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-text-secondary)' }}>
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </Link>
          <Link
            to="/faq"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px',
              borderRadius: 8,
              background: 'var(--color-surface)',
              color: 'var(--color-primary)',
              textDecoration: 'none',
              fontSize: 14,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              Help &amp; FAQ
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-text-secondary)' }}>
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </Link>
        </div>
      </section>

      {(signInOpen || showSignIn) && (
        <EmailSignIn
          reason="Sign in to sync your saved menus and order history across devices."
          onClose={() => {
            setSignInOpen(false);
            setShowSignIn(false);
          }}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['me'] });
            qc.invalidateQueries({ queryKey: ['saved'] });
            qc.invalidateQueries({ queryKey: ['orders'] });
          }}
        />
      )}

      <ConfirmDialog
        open={importPromptOpen}
        title={t('orders.import.title').replace('{n}', String(unsyncedCount))}
        body={t('orders.import.body')}
        confirmLabel={t('orders.import.confirm')}
        cancelLabel={t('orders.import.skip')}
        onCancel={() => {
          setImportPromptOpen(false);
          setImportDismissed(true);
        }}
        onConfirm={handleImport}
      />
    </div>
  );
}

interface PageProgressProps {
  shown: number;
  total: number;
  onShowMore: () => void;
}

// Combined progress + paging affordance — keeps users oriented in long lists.
function PageProgress({ shown, total, onShowMore }: PageProgressProps) {
  if (total === 0) return null;
  const fullyShown = shown >= total;
  return (
    <div
      style={{
        marginTop: 12,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 8,
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: 'var(--color-text-muted)',
          textAlign: 'center',
        }}
      >
        {fullyShown ? `All ${total}` : `Showing ${shown} of ${total}`}
      </div>
      {!fullyShown && (
        <button
          onClick={onShowMore}
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: 12,
            border: '1px solid var(--color-divider)',
            background: 'transparent',
            color: 'var(--color-primary)',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          Show more
        </button>
      )}
    </div>
  );
}

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--color-text-secondary)',
  paddingBottom: 12,
  margin: 0,
};

const sectionHeaderRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
};

const countBadge: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--color-text-muted)',
  fontVariantNumeric: 'tabular-nums',
  marginBottom: 12,
};

const listStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
